// src/Pages/Auth/Register.jsx
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import NeonButton from "../../components/ResponsiveButton/NeonButton.jsx";
// import { FcGoogle } from "react-icons/fc";
import { api, setAuthToken } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "react-toastify";
import logoDark from "../../assets/logo-buukkeri-dark.webp"; // logo

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
// Tulostettavat ASCII-merkit: 0x20 (space) – 0x7E (~)
const asciiOnly = (s) => /^[\x20-\x7E]+$/.test(s || "");

// Mapataan Zod/Backend-issues → kenttävirheiksi
const issuesToFieldErrors = (issues = []) => {
  const out = {};
  for (const i of issues) {
    const field = Array.isArray(i.path) ? i.path[0] : i.path || "";
    if (!field) continue;
    if (field === "email" && (i.code === "invalid_format" || i.message === "email_non_ascii")) {
      out.email = "Sähköposti ei kelpaa. Käytä muotoa nimi@osoite ja vältä ääkkösiä.";
    } else if (field === "password" && i.code === "too_small") {
      out.password = "Salasanassa pitää olla vähintään 8 merkkiä.";
    } else if (field === "display_name" && i.code === "too_small") {
      out.name = "Nimi on liian lyhyt.";
    } else {
      out[field] ||= "Tarkista kentän sisältö.";
    }
  }
  return out;
};

export default function Register() {
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");

  /* ------------ Validointi ------------ */
  const validate = (f) => {
    const e = {};
    if (!f.name.trim()) e.name = "Nimi vaaditaan.";
    if (!f.email.trim()) e.email = "Sähköposti vaaditaan.";
    else if (!asciiOnly(f.email)) e.email = "Käytä sähköpostissa vain perusmerkkejä (ei ä/ö/å).";
    else if (!emailRegex.test(f.email.trim())) e.email = "Sähköposti ei ole kelvollinen.";
    if (!f.password) e.password = "Salasana vaaditaan.";
    else if (f.password.length < 8) e.password = "Vähintään 8 merkkiä.";
    return e;
  };

  const runValidation = (next) => {
    const v = validate(next);
    setErrors(v);
    return v;
  };

  const handleChange = (e) => {
    const next = { ...form, [e.target.name]: e.target.value };
    setForm(next);
    if (touched[e.target.name]) runValidation(next);
  };

  const handleBlur = (e) => {
    const t = { ...touched, [e.target.name]: true };
    setTouched(t);
    runValidation(form);
  };

  const isValid = Object.keys(validate(form)).length === 0;

  /* ------------ Submit ------------ */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError("");

    const v = runValidation(form);
    if (Object.keys(v).length) return;

    setSubmitting(true);
    try {
      // 1) rekisteröinti
      const payload = {
        email: form.email.trim(),
        password: form.password,
        display_name: form.name.trim(),
      };

      const { token } = await api.auth.register(payload);
      if (token) setAuthToken(token);

      // 2) kirjautuminen (httpOnly-cookie) oikeaan API_BASEen
      try {
        await api.auth.login(payload.email, payload.password);
      } catch {}

      // 3b) ✉️ lähetä vahvistusmaili
      try {
        await api.auth.sendVerifyEmail({
          email: payload.email,
          name: payload.display_name,
        });
      } catch {}

      // 4) context + reititys
      try { await refresh?.(); } catch {}
      sessionStorage.setItem("showWelcomeToast", "1");
      toast.success("Boom! Tili luotu! Klikkaa sähköpostin vahvistuslinkkiä niin jatketaan.", { containerId: "global" });
      navigate("/verify-email", { replace: true });
    } catch (err) {
      const code = err?.payload?.error;

      if (err?.status === 409) {
        if (code === "email_taken") {
          setErrors((e0) => ({
            ...e0,
            email: "Hei, tällä osoitteella on jo tili! 🔑 Kirjaudu sisään – tai nollaa salasana.",
          }));
          setServerError("");
          setSubmitting(false);
          return;
        }
        toast.error("Hei, sulla taitaa olla jo tili 🎶 Kirjaudu sisään tai käytä eri tietoja.", {
          containerId: "global",
        });
        setServerError("");
        setSubmitting(false);
        return;
      }

      if (err?.status === 400 && code === "invalid_input") {
        const fieldErrs =
          (err.payload?.fieldErrors &&
            Object.fromEntries(
              Object.entries(err.payload.fieldErrors).map(([k, arr]) => [
                k === "display_name" ? "name" : k,
                arr?.[0],
              ])
            )) ||
          issuesToFieldErrors(err.payload?.issues || []);

        setErrors((e0) => ({ ...e0, ...fieldErrs }));
        setServerError("");
        setSubmitting(false);
        return;
      }

      const msg =
        (err?.payload && (err.payload.message || err.payload.error)) ||
        err?.message ||
        "Hups! Rekisteröinti jäi kesken 🪩 Koitetaan uusiksi.";
      setServerError(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  // const handleGoogleLogin = () => {
  //   if (!submitting) window.location.href = api.auth.googleUrl();
  // };

  const labelBase =
    "block font-customFont1 text-sm uppercase tracking-wide text-black/70 dark:text-white/80 mb-1";
  const inputBase =
    "w-full h-11 rounded-lg px-3 font-customFont1 bg-white dark:bg-[#0b0f1a] text-black/80 dark:text-white/90 border border-black/10 dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-pink/60 dark:focus:ring-primary text-base";
  const helpBase = "mt-1 text-xs";

  return (
    <div className="min-h-screen bg-lemon/60 dark:bg-black/40">
      <div
        className={[
          "mx-auto max-w-6xl px-4 pb-12",
          "pt-24",
          "md:minih-[calc(100vh-96px)] md:grid md:place-items-center",
        ].join(" ")}
      >
        <div className="w-full md:max-w-4xl lg:max-w-5xl mx-auto">

          {/* Logo – sama kuin loginissa */}
          <div className="mb-4 md:mb-6 flex justify-center">
            <Link to="/" aria-label="Siirry etusivulle" className="inline-flex items-center">
              <img
                src={logoDark}
                alt="Buukkeri"
                className="h-24 w-auto md:h-28"
                loading="lazy"
                decoding="async"
              />
            </Link>
          </div>

          {/* Otsikko keskelle myös mobiilissa */}
          <h1 className="text-2xl font-bold text-center text-black/80 dark:text-white/90 mb-3">
            Luo esiintyjätili
          </h1>

          {/* Kuvausteksti keskelle myös mobiilissa */}
          <p className="text-base md:text-lg leading-relaxed text-black/70 dark:text-white/80 text-center mb-6">
            Tee käyttäjätili ja aloita oman esiintyjäprofiilisi luonti.<br className="hidden md:inline" />
            Buukkerin kautta voit hallita esiintymisiäsi, päivittää kalenterisi ja vastaanottaa
            tarjouspyyntöjä suoraan tapahtumajärjestäjiltä.
          </p>

          <div
            className={[
              "grid grid-cols-1 gap-6",
              "md:grid-cols-2 md:items-start",
             
            ].join(" ")}
          >
            {/* Lomake */}
            <div className="order-2 md:order-2">
              <div className="rounded-xl ring-1 ring-black/10 dark:ring-white/10 bg-white/70 dark:bg-black/60 backdrop-blur-md shadow-lg w-full p-4 sm:p-5">
                <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                  {/* Nimi */}
                  <div>
                    <label className={labelBase} htmlFor="name">Nimi</label>
                    <input
                      id="name"
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={inputBase}
                      autoComplete="name"
                      required
                      minLength={1}
                      aria-invalid={!!(touched.name && errors.name)}
                      disabled={submitting}
                    />
                    {touched.name && errors.name && (
                      <p className={`${helpBase} text-red-600 dark:text-red-400`}>{errors.name}</p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label className={labelBase} htmlFor="email">Sähköposti</label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={inputBase}
                      autoComplete="email"
                      required
                      aria-invalid={!!(touched.email && errors.email)}
                      disabled={submitting}
                    />
                    {touched.email && errors.email && (
                      <p className={`${helpBase} text-red-600 dark:text-red-400`}>{errors.email}</p>
                    )}
                    <p className="mt-1 text-xs text-black/50 dark:text-white/50">
                      Sähköpostissa ei saa olla ääkkösiä..
                    </p>
                  </div>

                  {/* Salasana */}
                  <div>
                    <label className={labelBase} htmlFor="password">Salasana</label>
                    <input
                      id="password"
                      type="password"
                      name="password"
                      value={form.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={inputBase}
                      autoComplete="new-password"
                      required
                      minLength={8}
                      aria-invalid={!!(touched.password && errors.password)}
                      disabled={submitting}
                    />
                    <p className="mt-1 text-xs text-black/50 dark:text-white/50">
                      Vähintään 8 merkkiä – käytä sekä kirjaimia että numeroita.
                    </p>
                    {touched.password && errors.password && (
                      <p className={`${helpBase} text-red-600 dark:text-red-400`}>{errors.password}</p>
                    )}
                  </div>

                  {/* Serverivirhe */}
                  {serverError && (
                    <div className="rounded-md bg-red-100/70 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
                      {serverError}
                    </div>
                  )}

                  {/* Submit – sama inline-spinneri kuin Loginissa */}
                  <NeonButton
                    type="submit"
                    variant="primary"
                    size="md"
                    className="w-full flex items-center justify-center disabled:opacity-60"
                    disabled={submitting || !isValid}
                    aria-disabled={submitting || !isValid}
                  >
                    <span className="inline-flex items-center gap-2">
                      {submitting && (
                        <span className="relative inline-flex h-5 w-5 shrink-0">
                          <svg
                            className="animate-spin h-5 w-5 static"
                            style={{ position: "static" }}
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            role="status"
                            aria-label="Ladataan"
                            focusable="false"
                            aria-hidden="true"
                          >
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25" />
                            <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
                          </svg>
                        </span>
                      )}
                      <span>{submitting ? "Luodaan tili…" : "Rekisteröidy"}</span>
                    </span>
                  </NeonButton>
                </form>

                <p className="mt-6 text-center text-sm text-black/60 dark:text-white/60">
                  Onko sinulla jo tili?{" "}
                  <button
                    onClick={() => navigate("/kirjaudu")}
                    className="text-pink underline hover:opacity-80 dark:text-primary"
                  >
                    Kirjaudu sisään
                  </button>
                </p>
              </div>
            </div>

            {/* Info/CTA */}
            <div className="order-1 md:order-1">
              <div className="rounded-xl ring-1 ring-black/10 dark:ring-white/10 bg-white/70 dark:bg-black/50 backdrop-blur p-4">
                <p className="text-black/80 dark:text-white/80 text-sm leading-relaxed text-center">
                  Haluatko tietää tarkemmin, miten liittyminen toimii?{" "}
                  Tutustu{" "}
                  <Link
                    to="/ukk-esiintyjille"
                    className="font-semibold underline text-pink dark:text-primary hover:opacity-90"
                  >
                    Esiintyjien UKK -sivuun
                  </Link>
                  , josta löydät vastaukset yleisimpiin kysymyksiin.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
